import { API_METHODS } from '@compass/client/constants/api-methods';
import type { Program } from '@compass/client/types/program';
import { membershipService } from '@services/api';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { WorkflowType } from '@/constants/dispositions';
import { buildQueriesFingerprint } from '@/hooks/internal/useStableFold';
import { useFeatureFlagState } from '@/hooks/useFeatureFlag';
import { permissionMapQueryKey } from '@/hooks/usePermissions';
import { useMyManagedTeams } from '@/pages/workforce/hooks/useMyManagedTeams';
import { useStore } from '@/store/store';

/**
 * Global dynconfig flag (`feature_flag` namespace) that switches the Workforce
 * Console to permission-based access (BV-2303). Off or absent: the console keeps
 * the team-lead rule it had before, and nothing below the flag is read.
 */
const PERMISSIONS_BASED_ACCESS_FLAG = 'permissions_based_wc_access';

/**
 * Per-organization blocklist flag, read only when permission-based access is
 * on: `true` hides the console for that organization. Same convention as
 * `clone_case_<org_id>`.
 */
const hideWorkforceConsoleFlagKey = (organizationId: string | null): string | undefined =>
  organizationId ? `hide_workforce_console_${organizationId}` : undefined;

/**
 * Workforce Console APIs whose access depends on the user's role: listing a
 * program's cases, reading its workflows (SLA and state labels), listing its
 * teams (the team switcher) and reassigning its cases.
 *
 * The console also calls GetCurrentUserPrograms, LookupUsers, GetMyAvailability
 * and ListPayors, but every logged-in user may call those (own data or shared
 * reference data), and the permission map cannot vouch for them: it omits
 * matches that are only owned/associated, and reports GetCurrentUserPrograms as
 * false for program-level roles even though the request succeeds. Requiring
 * them would hide the console from roles that can use it.
 */
const WORKFORCE_CONSOLE_METHODS = [
  API_METHODS.CaseRecordsService.FindCaseRecords,
  API_METHODS.WorkflowService.GetWorkflowsByProgram,
  API_METHODS.ProgramTeamService.ListProgramTeams,
  API_METHODS.CaseRecordsService.UpdateAssigned,
];

const grantsWorkforceConsole = (permissions: Record<string, boolean> | undefined): boolean =>
  !!permissions && WORKFORCE_CONSOLE_METHODS.every(method => permissions[method] === true);

/**
 * One CheckMyPermissions call for one program, filtered to the console's methods
 * and cached under the permission-map key prefix (so role changes that invalidate
 * the map also invalidate these). The sidebar's selected-program check and the
 * console page share these keys, so each program is asked at most once.
 */
const workforcePermissionsQuery = (organizationId: string | null, programId: string) => ({
  queryKey: [...permissionMapQueryKey, 'workforce', organizationId ?? '', programId] as const,
  queryFn: () =>
    membershipService.checkMyPermissions({
      organization_id: organizationId ?? undefined,
      program_id: programId,
      methods: WORKFORCE_CONSOLE_METHODS,
    }),
  staleTime: 2 * 60 * 1000,
  gcTime: 5 * 60 * 1000,
  refetchOnWindowFocus: true,
});

/** The selected organization's programs, in store order. */
const useOrgPrograms = (): Program[] => {
  const programsById = useStore(state => state.programs);
  const programIds = useStore(state => state.programIds);
  return useMemo(
    () =>
      programIds
        .map(id => programsById[id])
        .filter((program): program is Program & { id: string } => !!program?.id),
    [programIds, programsById],
  );
};

/**
 * The subset of `programs` whose permission map grants every role-dependent
 * console API. One CheckMyPermissions call per program; `enabled: false` asks
 * nothing.
 */
const useGrantedPrograms = (
  programs: Program[],
  enabled: boolean,
): { programs: Program[]; isLoading: boolean } => {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const active = enabled && !!organizationId;

  const queries = useQueries({
    queries: programs.map(program => ({
      ...workforcePermissionsQuery(organizationId, program.id ?? ''),
      enabled: active,
    })),
  });

  const fingerprint = buildQueriesFingerprint(
    queries,
    q => Object.keys(q.data?.permissions ?? {}).length,
  );
  const granted = useMemo(
    () => programs.filter((_, i) => grantsWorkforceConsole(queries[i]?.data?.permissions)),
    // `fingerprint` stands in for `queries`, whose identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programs, fingerprint],
  );

  return { programs: granted, isLoading: active && queries.some(q => q.isLoading) };
};

/**
 * Programs of the selected organization in which the user may use the Workforce
 * Console: the permission map for that program grants every role-dependent
 * console API. A missing key reads as denied, so a program where the user only
 * has assigned-scope case access (Case Manager) or no reassign (view-only
 * roles) is excluded. Org-level and global admins pass in every program.
 * `enabled: false` (team-lead mode) asks nothing and returns no programs.
 */
export const useWorkforceEligiblePrograms = (
  enabled: boolean,
): { programs: Program[]; isLoading: boolean } => useGrantedPrograms(useOrgPrograms(), enabled);

/**
 * Which access rule the Workforce Console follows, from the
 * `permissions_based_wc_access` flag: `permissionsBased` is true only when the
 * flag is explicitly on, so an absent flag, a failed read and the first load
 * all mean the team-lead rule. `isLoading` is true while the flag is first read.
 */
export const useWorkforceAccessMode = (): { permissionsBased: boolean; isLoading: boolean } => {
  const { enabled, isLoading } = useFeatureFlagState(PERMISSIONS_BASED_ACCESS_FLAG);
  return { permissionsBased: enabled, isLoading };
};

/**
 * The console check for the selected program, asked only when that program
 * belongs to the selected organization. `settled` latches on `isFetched` (see
 * `useConsoleEligibility`) and is true when nothing is asked.
 */
const useSelectedProgramCheck = (
  enabled: boolean,
  orgPrograms: Program[],
): { grants: boolean; settled: boolean } => {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const selectedInOrg = orgPrograms.some(program => program.id === selectedProgramId);
  const active = enabled && !!organizationId && selectedInOrg;
  const selected = useQuery({
    ...workforcePermissionsQuery(organizationId, selectedProgramId ?? ''),
    enabled: active,
  });
  return {
    grants: grantsWorkforceConsole(selected.data?.permissions),
    settled: !active || selected.isFetched,
  };
};

/**
 * Whether the user may use the console somewhere in the selected organization,
 * asking as little as possible on every page: the selected program first, and
 * the org's other programs only once that check has finished without a grant.
 *
 * The fallback waits on `isFetched`, not `isLoading`: a refetch of a failed
 * check without data goes back to loading, and gating on that would switch the
 * fallback off and on again on every failure, refetching in a loop. The
 * fallback also leaves out the selected program, so it never shares a query
 * with the check it waits on. A persisted program id from another org is not
 * asked about. `enabled: false` asks nothing.
 */
const useConsoleEligibility = (enabled: boolean): { isEligible: boolean; isLoading: boolean } => {
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const orgPrograms = useOrgPrograms();
  const otherPrograms = useMemo(
    () => orgPrograms.filter(program => program.id !== selectedProgramId),
    [orgPrograms, selectedProgramId],
  );

  const selected = useSelectedProgramCheck(enabled, orgPrograms);
  const others = useGrantedPrograms(otherPrograms, enabled && selected.settled && !selected.grants);

  const isEligible = selected.grants || others.programs.length > 0;
  return {
    isEligible,
    // Stop waiting as soon as one program grants the console.
    isLoading: !isEligible && (!selected.settled || others.isLoading),
  };
};

/** Whether the selected program has a PQC workflow (Knipper organization). */
const useHasPqcWorkflow = (): boolean => {
  const { getWorkflowsArray } = useStore();
  const workflows = getWorkflowsArray();
  return workflows?.some(workflow => workflow.workflow_type === WorkflowType.PQC) ?? false;
};

/**
 * Whether the Workforce Console should be shown in the sidebar and reachable by
 * URL. Shared by the sidebar entry and the route guard so the two never disagree.
 *
 * `permissions_based_wc_access` off or absent (`useWorkforceAccessMode`): shown
 * when the user leads at least one team in the selected organization
 * (`useMyManagedTeams`), exactly as before BV-2303.
 *
 * Flag on: shown only when all hold:
 *   1. `hide_workforce_console_<org_id>` is not on for the selected
 *      organization (absent, `false` or unreadable has no effect);
 *   2. the user may use the console in at least one program of that
 *      organization (`useConsoleEligibility`: the selected program is asked
 *      first, so an admin costs one call, not one per program, on every page);
 *   3. the selected program has no PQC workflow (Knipper organization), the
 *      same rule as My Cases and Campaigns.
 *
 * Nothing is shown while the flags or checks load. The team-lead check starts
 * alongside the mode flag, so the default mode costs no extra round trip; the
 * hide flag and the permission checks are only read in permission-based mode,
 * and no permission is checked for a hidden organization.
 *
 * UI gate only: every API still enforces its own permission server-side.
 */
export const useShouldShowWorkforceConsole = (): { shouldShow: boolean; isLoading: boolean } => {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const hasPqcWorkflow = useHasPqcWorkflow();
  const mode = useWorkforceAccessMode();
  const leads = useMyManagedTeams({ enabled: !mode.permissionsBased });
  const hideFlag = useFeatureFlagState(
    mode.permissionsBased ? hideWorkforceConsoleFlagKey(organizationId) : undefined,
  );
  const eligibility = useConsoleEligibility(
    mode.permissionsBased && !hideFlag.isLoading && !hideFlag.enabled,
  );

  if (mode.isLoading) return { shouldShow: false, isLoading: true };
  if (!mode.permissionsBased) {
    return { shouldShow: leads.isManagerOfAny, isLoading: leads.isLoading };
  }
  if (hideFlag.isLoading) return { shouldShow: false, isLoading: true };
  if (hideFlag.enabled) return { shouldShow: false, isLoading: false };
  return {
    shouldShow: eligibility.isEligible && !hasPqcWorkflow,
    isLoading: eligibility.isLoading,
  };
};
